'use client';

import type { PlaceAutocompleteType } from '@googlemaps/google-maps-services-js';
import { useVolvoCarsUrl } from '@vcc-www/volvo-cars-url';
import { getMarketSite } from '@volvo-cars/market-sites';
import type { RegionCode } from '@volvo-cars/market-sites';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchFromDealerLocator } from '../../utils/fetchFromDealerLocator';
import { countriesMapping } from '../../utils/getCountryCodes';
import { ADDRESS_AUTOCOMPLETE_ENDPOINT } from '../constants/apiEndpoints';
import { useStore } from '../providers/StoreProvider';
import type { AutocompleteResponse } from '../types/maps';

function getComponents(regionCode: RegionCode) {
  if (countriesMapping.has(regionCode)) {
    return countriesMapping.get(regionCode)?.map((rc) => `country:${rc}`);
  }
  return [`country:${regionCode}`];
}
type useAddressAutocompleteResponse = {
  data?: AutocompleteResponse['data'];
  requestSearchInput?: string;
};

const fetchAutocompleteSuggestions = (
  input: string,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  params: Record<string, any>,
) => {
  const allParams = { ...params, input };
  return fetchFromDealerLocator<AutocompleteResponse>(
    ADDRESS_AUTOCOMPLETE_ENDPOINT,
    allParams,
  );
};

const DEBOUNCE_TIME = 500;
const useDebounce = (input: string, callback: (input: string) => void) => {
  const intervalHandle = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (input !== '') {
      // Do an initial search without debouncing on first keypress
      if (input.trim().length === 1) {
        callback(input);
      } else {
        intervalHandle.current = setTimeout(() => {
          callback(input);
        }, DEBOUNCE_TIME);
      }
    }

    return () => clearTimeout(intervalHandle.current);
  }, [input, callback]);
};

export function useAddressAutocomplete(
  input: string,
  useZipCode: boolean,
  skip = false,
  resetPredictions = false,
): useAddressAutocompleteResponse {
  const [data, setData] = useState<AutocompleteResponse>();
  const { siteSlug } = useVolvoCarsUrl();
  const { regionCode, locale } = getMarketSite(siteSlug);
  const { sessiontoken } = useStore();
  const shouldUseComponents = !!regionCode && regionCode !== '001';

  const params = useMemo(
    () => ({
      sessiontoken,
      language: locale,
      radius: 10000,
      ...(shouldUseComponents && {
        components: getComponents(regionCode),
      }),
      // postal_code is allowed according to gmapi but the types here do not allow it so we must use type casting to force it.
      ...(useZipCode && { types: 'postal_code' as PlaceAutocompleteType }),
    }),
    [sessiontoken, locale, regionCode, shouldUseComponents, useZipCode],
  );

  const fetchData = useCallback(
    async (searchString: string) => {
      if (skip) {
        return;
      }

      if (resetPredictions) {
        setData(undefined);
      } else {
        const { data: newData } = await fetchAutocompleteSuggestions(
          searchString,
          params,
        );
        if (newData) {
          setData(newData);
        }
      }
    },
    [skip, resetPredictions, params],
  );

  useDebounce(input, fetchData);

  if (!input) {
    return {
      data: undefined,
      requestSearchInput: undefined,
    };
  }
  return {
    data: data?.data,
    requestSearchInput: data?.requestSearchInput,
  };
}
